import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon, 
  PlusIcon, 
  ChatBubbleLeftRightIcon, 
  ClockIcon,
  CalendarIcon, 
  UserIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAppData } from '../../contexts/AppDataContext';

const SupplierNegotiations = ({ suppliers, currentSupplierId }) => {
  const { fetchAllData } = useAppData();
  const [showNewNegotiationModal, setShowNewNegotiationModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState(currentSupplierId || '');
  const [currentNegotiationId, setCurrentNegotiationId] = useState(null);
  const [negotiationData, setNegotiationData] = useState({
    title: '',
    objective: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    status: 'in_progress',
    notes: ''
  });

  // Obtenir les données des négociations sauvegardées
  const getSavedNegotiations = () => {
    try {
      return JSON.parse(localStorage.getItem('stockpro_negotiations')) || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des négociations:', error);
      return [];
    }
  };

  // Données de négociations - seulement des données réelles depuis localStorage
  const [negotiations, setNegotiations] = useState([]);

  // Utiliser uniquement des contrats réels depuis localStorage
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { getPurchases } = useAppData();

  // Charger les contrats depuis localStorage
  useEffect(() => {
    try {
      setIsLoading(true);
      // Charger les contrats existants
      const storedContracts = JSON.parse(localStorage.getItem('stockpro_contracts')) || [];
      setContracts(storedContracts);
      
      // Générer des contrats à partir des commandes d'achat si nécessaire
      if (storedContracts.length === 0) {
        const purchases = getPurchases() || [];
        if (purchases.length > 0) {
          console.log('Génération de contrats à partir des commandes:', purchases.length);
          
          // Regrouper les achats par fournisseur
          const supplierPurchases = {};
          purchases.forEach(purchase => {
            if (purchase.supplierId || purchase.supplier) {
              const supplierId = purchase.supplierId || `SUP-${Math.random().toString(36).substr(2, 6)}`;
              const supplierName = purchase.supplier || 'Fournisseur';
              
              if (!supplierPurchases[supplierId]) {
                supplierPurchases[supplierId] = {
                  supplier: { id: supplierId, name: supplierName },
                  purchases: []
                };
              }
              
              supplierPurchases[supplierId].purchases.push(purchase);
            }
          });
          
          // Créer des contrats pour chaque fournisseur avec des achats récurrents
          const generatedContracts = [];
          Object.values(supplierPurchases).forEach((data, index) => {
            if (data.purchases.length >= 2) {
              const supplier = data.supplier;
              const today = new Date();
              const startDate = new Date();
              startDate.setMonth(startDate.getMonth() - 3);
              
              const endDate = new Date();
              endDate.setFullYear(endDate.getFullYear() + 1);
              
              const totalValue = data.purchases.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
              
              generatedContracts.push({
                id: `CTR-${index + 100}`,
                supplierId: supplier.id,
                supplierName: supplier.name,
                title: `Contrat ${supplier.name} ${today.getFullYear()}`,
                startDate: startDate.toISOString().split('T')[0],
                endDate: endDate.toISOString().split('T')[0],
                value: `${Math.round(totalValue).toLocaleString()} DA`,
                status: 'active',
                document: `contrat_${supplier.name.toLowerCase().replace(/\s/g, '_')}_${today.getFullYear()}.pdf`
              });
            }
          });
          
          if (generatedContracts.length > 0) {
            // Sauvegarder les contrats générés
            localStorage.setItem('stockpro_contracts', JSON.stringify(generatedContracts));
            setContracts(generatedContracts);
            console.log(`${generatedContracts.length} contrats générés à partir des données d'achat`);
          }
        }
      } else {
        console.log(`${storedContracts.length} contrats chargés depuis localStorage`);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des contrats:', error);
      setContracts([]);
    } finally {
      setIsLoading(false);
    }
  }, [getPurchases]);

  // Charger les données depuis localStorage et générer des négociations initiales si nécessaire
  useEffect(() => {
    setIsLoading(true);
    
    try {
      // Récupérer les négociations sauvegardées
      const savedNegotiations = getSavedNegotiations();
      
      if (savedNegotiations.length > 0) {
        console.log('Négociations chargées depuis localStorage:', savedNegotiations.length);
        setNegotiations(savedNegotiations);
      } else {
        // Si aucune négociation n'existe, générer des données initiales à partir des contrats et des fournisseurs
        console.log('Aucune négociation trouvée, génération des données initiales');
        
        // Récupérer les fournisseurs actifs
        const activeSuppliers = suppliers.filter(s => s.status === 'Active' || s.status === 'active');
        
        // Générer des négociations automatiquement si des fournisseurs existent
        if (activeSuppliers.length > 0) {
          const initialNegotiations = [];
          
          // Pour chaque fournisseur avec au moins deux achats, créer une négociation
          activeSuppliers.forEach((supplier, index) => {
            // Types de négociations courantes
            const negotiationTypes = [
              { title: 'Renouvellement contrat', objective: 'Obtenir une réduction de prix' },
              { title: 'Amélioration des délais', objective: 'Réduire le temps de livraison' },
              { title: 'Conditions de paiement', objective: 'Étendre le délai de paiement à 60 jours' }
            ];
            
            // Si ce n'est pas le premier fournisseur, créer une négociation en cours ou planifiée
            if (index > 0 && index < 3) {
              const negotiationType = negotiationTypes[index % negotiationTypes.length];
              const today = new Date();
              let startDate, status;
              
              if (index === 1) {
                // En cours
                startDate = new Date();
                startDate.setDate(startDate.getDate() - 15);
                status = 'in_progress';
              } else {
                // Planifiée
                startDate = new Date();
                startDate.setDate(startDate.getDate() + 15);
                status = 'planned';
              }
              
              initialNegotiations.push({
                id: `NEG-INIT-${index + 1}`,
                supplierId: supplier.id,
                supplierName: supplier.name,
                title: `${negotiationType.title} ${supplier.name}`,
                objective: negotiationType.objective,
                startDate: startDate.toISOString().split('T')[0],
                status: status,
                contacts: [],
                createdAt: new Date().toISOString(),
                lastUpdated: new Date().toISOString()
              });
            }
          });
          
          if (initialNegotiations.length > 0) {
            localStorage.setItem('stockpro_negotiations', JSON.stringify(initialNegotiations));
            setNegotiations(initialNegotiations);
            console.log(`${initialNegotiations.length} négociations initiales générées`);
          }
        }
      }
      
      // Préremplir le fournisseur si on vient de la page détail
      if (currentSupplierId) {
        setSelectedSupplierId(currentSupplierId);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des négociations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentSupplierId, suppliers]);
  
  // Sauvegarder les négociations dans localStorage
  useEffect(() => {
    if (negotiations.length > 0) {
      localStorage.setItem('stockpro_negotiations', JSON.stringify(negotiations));
      console.log('Négociations sauvegardées dans localStorage');
    }
  }, [negotiations]);

  // Statistiques des négociations - calculées dynamiquement
  const calculateStats = () => {
    // Filtrer par fournisseur si nécessaire
    let filteredNegotiations = negotiations;
    if (currentSupplierId) {
      filteredNegotiations = negotiations.filter(n => n.supplierId === currentSupplierId);
    }
    
    const completed = filteredNegotiations.filter(n => n.status === 'completed');
    
    // Calculer l'économie moyenne réelle si possible
    let avgSaving = '0.0%';
    if (completed.length > 0) {
      // Compter uniquement les négociations avec prix initial et final
      const negotiationsWithSavings = completed.filter(n => 
        n.initialPrice && n.finalPrice && n.initialPrice > n.finalPrice
      );
      
      if (negotiationsWithSavings.length > 0) {
        // Calculer le pourcentage d'économie moyen
        const savingSum = negotiationsWithSavings.reduce((sum, n) => {
          return sum + ((n.initialPrice - n.finalPrice) / n.initialPrice);
        }, 0);
        
        avgSaving = `${(savingSum / negotiationsWithSavings.length * 100).toFixed(1)}%`;
      }
    }
    
    return {
      totalNegotiations: filteredNegotiations.length,
      completed: completed.length,
      inProgress: filteredNegotiations.filter(n => n.status === 'in_progress').length,
      planned: filteredNegotiations.filter(n => n.status === 'planned').length,
      avgSaving
    };
  };
  
  const stats = calculateStats();

  // Filtrer les négociations par statut
  const filterNegotiationsByStatus = (status) => {
    if (currentSupplierId) {
      // Si on est sur une page de fournisseur spécifique, filtrer d'abord par fournisseur
      return negotiations.filter(n => n.supplierId === currentSupplierId && n.status === status);
    }
    return negotiations.filter(n => n.status === status);
  };

  // Gérer l'édition d'une négociation
  const handleEditNegotiation = (negotiation) => {
    setIsEditMode(true);
    setCurrentNegotiationId(negotiation.id);
    setSelectedSupplierId(negotiation.supplierId);
    setNegotiationData({
      title: negotiation.title || '',
      objective: negotiation.objective || '',
      startDate: negotiation.startDate || new Date().toISOString().split('T')[0],
      endDate: negotiation.endDate || '',
      status: negotiation.status || 'in_progress',
      notes: negotiation.notes || ''
    });
    setShowNewNegotiationModal(true);
  };

  // Gérer la suppression d'une négociation
  const handleDeleteNegotiation = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette négociation ?')) {
      try {
        const updatedNegotiations = negotiations.filter(neg => neg.id !== id);
        setNegotiations(updatedNegotiations);
        localStorage.setItem('stockpro_negotiations', JSON.stringify(updatedNegotiations));
        toast.success('Négociation supprimée avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression de la négociation:', error);
        toast.error('Erreur lors de la suppression de la négociation');
      }
    }
  };

  // Créer ou mettre à jour une négociation
  const handleCreateNegotiation = () => {
    if (!selectedSupplierId || !negotiationData.title || !negotiationData.objective || !negotiationData.startDate) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const selectedSupplier = suppliers.find(s => s.id === selectedSupplierId);
    
    if (!selectedSupplier) {
      toast.error('Fournisseur non trouvé');
      return;
    }

    try {
      let updatedNegotiations;
      
      if (isEditMode && currentNegotiationId) {
        // Mise à jour d'une négociation existante
        updatedNegotiations = negotiations.map(neg => {
          if (neg.id === currentNegotiationId) {
            return {
              ...neg,
              supplierId: selectedSupplierId,
              supplierName: selectedSupplier.name,
              title: negotiationData.title,
              objective: negotiationData.objective,
              startDate: negotiationData.startDate,
              endDate: negotiationData.endDate || null,
              status: negotiationData.status,
              notes: negotiationData.notes,
              lastUpdated: new Date().toISOString()
            };
          }
          return neg;
        });
        toast.success('Négociation mise à jour avec succès');
      } else {
        // Créer une nouvelle négociation
        // Générer un ID unique avec timestamp pour éviter les collisions
        const timestamp = new Date().getTime();
        const uniqueId = `NEG-${timestamp.toString().slice(-6)}-${String(negotiations.length + 1).padStart(3, '0')}`;

        const newNegotiation = {
          id: uniqueId,
          supplierId: selectedSupplierId,
          supplierName: selectedSupplier.name,
          title: negotiationData.title,
          objective: negotiationData.objective,
          startDate: negotiationData.startDate,
          endDate: negotiationData.endDate || null,
          status: negotiationData.status,
          notes: negotiationData.notes,
          contacts: [],
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        };

        // Ajouter la négociation
        updatedNegotiations = [...negotiations, newNegotiation];
        toast.success('Nouvelle négociation créée');
      }
      
      // Mettre à jour l'état et sauvegarder dans localStorage
      setNegotiations(updatedNegotiations);
      localStorage.setItem('stockpro_negotiations', JSON.stringify(updatedNegotiations));
      
      // Actualiser les données globales
      fetchAllData();
      
      // Réinitialiser et fermer le modal
      setIsEditMode(false);
      setCurrentNegotiationId(null);
      setShowNewNegotiationModal(false);
      resetNegotiationForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la négociation:', error);
      toast.error('Erreur lors de la sauvegarde de la négociation');
    }
  };

  // Réinitialiser le formulaire
  const resetNegotiationForm = () => {
    setSelectedSupplierId('');
    setNegotiationData({
      title: '',
      objective: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      status: 'in_progress',
      notes: ''
    });
  };

  // Obtenir la couleur en fonction du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-400';
      case 'in_progress':
        return 'bg-blue-500/20 text-blue-400';
      case 'planned':
        return 'bg-amber-500/20 text-amber-400';
      case 'expired':
        return 'bg-red-500/20 text-red-400';
      case 'active':
        return 'bg-green-500/20 text-green-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  // Obtenir le texte en fonction du statut
  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Terminée';
      case 'in_progress':
        return 'En cours';
      case 'planned':
        return 'Planifiée';
      case 'expired':
        return 'Expirée';
      case 'active':
        return 'Actif';
      default:
        return status;
    }
  };

  // Cette variable n'est plus nécessaire car nous trouvons dynamiquement la dernière négociation complétée

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <p className="text-white/60 text-sm font-medium">Total Négociations</p>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-600">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-2xl font-bold text-white mt-2">{stats.totalNegotiations}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <p className="text-white/60 text-sm font-medium">Terminées</p>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-green-600 to-emerald-600">
              <CheckCircleIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-2xl font-bold text-white mt-2">{stats.completed}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <p className="text-white/60 text-sm font-medium">En cours</p>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-600">
              <ClockIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-2xl font-bold text-white mt-2">{stats.inProgress}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <p className="text-white/60 text-sm font-medium">Planifiées</p>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-amber-600 to-orange-600">
              <CalendarIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-2xl font-bold text-white mt-2">{stats.planned}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <p className="text-white/60 text-sm font-medium">Économie moyenne</p>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600">
              <ArrowPathIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-2xl font-bold text-white mt-2">{stats.avgSaving}</p>
        </motion.div>
      </div>

      {/* Sections */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Négociations en cours</h2>
          <button
            onClick={() => setShowNewNegotiationModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Nouvelle</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-white/10">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Fournisseur</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Titre</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Démarré le</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                    <p className="mt-2 text-white/70">Chargement des négociations...</p>
                  </td>
                </tr>
              ) : filterNegotiationsByStatus('in_progress').length > 0 ? (
                filterNegotiationsByStatus('in_progress').map((negotiation, index) => (
                  <tr key={negotiation.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{negotiation.supplierName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{negotiation.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-white/80">{negotiation.startDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(negotiation.status)}`}>
                        {getStatusText(negotiation.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center space-x-2">
                        <button 
                          onClick={() => handleEditNegotiation(negotiation)}
                          className="text-indigo-400 hover:text-indigo-300 transition-colors"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleDeleteNegotiation(negotiation.id)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <ExclamationCircleIcon className="h-10 w-10 text-white/40 mb-2" />
                      <p className="text-white/60 mb-4">Aucune négociation en cours</p>
                      <button
                        onClick={() => setShowNewNegotiationModal(true)}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-colors"
                      >
                        Démarrer une négociation
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>





      {/* Modal pour créer une nouvelle négociation */}
      {showNewNegotiationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-gray-900 rounded-3xl border border-white/20 w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between sticky top-0 bg-gray-900 py-2">
              <h3 className="text-xl font-semibold text-white">
                {isEditMode ? 'Modifier la négociation' : 'Nouvelle négociation'}
              </h3>
              <button 
                onClick={() => {
                  setShowNewNegotiationModal(false);
                  setIsEditMode(false);
                  setCurrentNegotiationId(null);
                  resetNegotiationForm();
                }}
                className="text-white/60 hover:text-white p-2"
              >
                &times;
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Fournisseur <span className="text-red-400">*</span></label>
                <select
                  value={selectedSupplierId}
                  onChange={(e) => setSelectedSupplierId(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Sélectionner un fournisseur</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Titre <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={negotiationData.title}
                  onChange={(e) => setNegotiationData({...negotiationData, title: e.target.value})}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Renouvellement contrat..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Objectif <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={negotiationData.objective}
                  onChange={(e) => setNegotiationData({...negotiationData, objective: e.target.value})}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Obtenir une réduction de 10%..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Date de début <span className="text-red-400">*</span></label>
                  <input
                    type="date"
                    value={negotiationData.startDate}
                    onChange={(e) => setNegotiationData({...negotiationData, startDate: e.target.value})}
                    className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Date de fin (optionnelle)</label>
                  <input
                    type="date"
                    value={negotiationData.endDate}
                    onChange={(e) => setNegotiationData({...negotiationData, endDate: e.target.value})}
                    className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Statut</label>
                <select
                  value={negotiationData.status}
                  onChange={(e) => setNegotiationData({...negotiationData, status: e.target.value})}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="planned">Planifiée</option>
                  <option value="in_progress">En cours</option>
                  <option value="completed">Terminée</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Notes</label>
                <textarea
                  value={negotiationData.notes}
                  onChange={(e) => setNegotiationData({...negotiationData, notes: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Notes ou détails supplémentaires..."
                ></textarea>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 sticky bottom-0 bg-gray-900 py-3">
              <button
                onClick={() => {
                  setShowNewNegotiationModal(false);
                  setIsEditMode(false);
                  setCurrentNegotiationId(null);
                  resetNegotiationForm();
                }}
                className="w-full sm:w-auto px-4 py-2.5 border border-white/20 rounded-xl text-white hover:bg-white/10 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateNegotiation}
                className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-colors"
              >
                {isEditMode ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierNegotiations; 